// platform.collection — keyed JSON documents at a stable path.
//
// The complement to `app.log()` (which is append-only ULID-keyed). A
// collection's entries are MUTABLE, keyed by something app-meaningful
// (a slug, a sub, a name), and laid out at:
//
//   {bucketPrefix}data/{name}/{key}/{filename}
//
// where {filename} defaults to `index.json`. The trailing-file shape
// (rather than `{name}/{key}.json` directly) is so apps can attach
// sibling files later — e.g. attachments at `projects/foo/avatar.png`
// alongside `projects/foo/manifest.json` — without breaking the list
// pattern.
//
// All reads/writes are JSON. Encryption isn't wired into this surface
// in v1: encrypted collections need a separate think on "who can update
// an encrypted manifest" (see SDK_REDESIGN discussion). The log()
// primitive carries encryption today.
//
// PARTITIONING: when `deps.partitionPrefix` is set, every record's full
// key is `{partition}/{key}` — `put` derives the partition segment from
// the resolver, so writes land in the caller's own partition and an IAM
// grant on `{base}{sub}/*` can enforce "write own records" at the S3
// API. Reads are unchanged: `scan`/`list`/`subscribe` cover ALL
// partitions (they nest under the same base prefix), and `get`/`update`/
// `delete` take full `{partition}/{key}` keys — exactly what `put`
// returns and what change events carry. `partition(id)` returns a view
// scoped to one partition where bare keys work everywhere. This layer
// stays session-agnostic: the resolver is injected by the caller (the
// SDK binds it to the signed-in user's sub; docs/PERMISSIONS-SPEC.md).

import {
  DeleteObjectCommand,
  GetObjectCommand,
  HeadObjectCommand,
  ListObjectsV2Command,
  PutObjectCommand,
  type S3Client,
} from "https://cdn.jsdelivr.net/npm/@aws-sdk/client-s3@3.1054.0/+esm";
import { reportScanCount } from "./dev-guards.ts";

export type KeyDerivation<T> = string | ((doc: T) => string);

/** Secondary index — write-time duplicates a tiny pointer doc under a
 *  parallel prefix so we can look up by a non-primary field without
 *  scanning. Each pointer stores just the primary key. */
export interface CollectionIndex<T> {
  /** Display name, also used as the path suffix
   *  (`{collection-name}-by-{index-name}/`). */
  name: string;
  /** Which field to index. Either a field name or a derivation fn. */
  field: keyof T & string;
  /** When true, the pointer write uses `If-None-Match: *` so a duplicate
   *  value fails the put. Default false. */
  unique?: boolean;
}

export interface CollectionConfig<T> {
  /** Trailing filename for each entry. Default: `"index.json"`. */
  filename?: string;
  /** How to derive the path key from a doc passed to `put(doc)`.
   *  Either a field name (`"slug"` → reads `doc.slug` as string) or a
   *  function. If omitted, `put(doc)` must be called with an explicit
   *  key: `put("my-key", doc)`. */
  key?: KeyDerivation<T>;
  /** Secondary indexes. Each maintains a parallel pointer prefix that
   *  the SDK writes to alongside the main doc. */
  indexes?: CollectionIndex<T>[];
  /** Declare this store CLAIM-SHAPED: every `put` is put-if-absent
   *  (`If-None-Match: *`), so putting an existing key throws instead of
   *  overwriting. Pair with a `create` grant in s3p.permissions.json and the
   *  no-overwrite rule is enforced by S3 itself (the grant's IAM condition
   *  REQUIRES the header, so raw un-conditional puts are denied too) — the
   *  claim-a-name pattern: handles, seat reservations, idempotency keys.
   *  This flag describes the STORE, not the caller: a role holding plain
   *  `write` on it can still fix a bad claim via delete + re-put (or an
   *  overwrite through its own non-createOnly handle). */
  createOnly?: boolean;
}

export interface CollectionWhere<T> {
  list(): Promise<T[]>;
  /** Chain additional filters. Currently only one is supported in v1
   *  but the shape is here so callers don't refactor when chaining lands. */
  where(pred: (doc: T) => boolean): CollectionWhere<T>;
}

export interface Collection<T> {
  /** Walk the collection lazily, decoding each doc from JSON. */
  scan(): AsyncIterable<T>;
  /** Materialize everything as an array. Use for finite collections. */
  list(): Promise<T[]>;
  /** In-memory filter. `.where(p).list()` is the common form. */
  where(pred: (doc: T) => boolean): CollectionWhere<T>;
  /** Fetch by key. Returns null if missing. */
  get(key: string): Promise<T | null>;
  /** Existence check via GET (no HEAD on STS sessions here). */
  has(key: string): Promise<boolean>;
  /** Upsert. With config.key set: `put(doc)` derives the key.
   *  Without it: `put(key, doc)` is required. */
  put(doc: T): Promise<{ key: string; path: string }>;
  put(key: string, doc: T): Promise<{ key: string; path: string }>;
  /** Remove by key. */
  delete(key: string): Promise<void>;
  /** Look up a doc by an indexed field value. Requires `indexes:` in
   *  the collection config that names the same `indexName`. */
  findBy(indexName: string, value: string): Promise<T | null>;
  /** Optimistic concurrency update: GETs the doc with its ETag, calls
   *  `mutator(value)`, PUTs back with `If-Match: <etag>`. On
   *  PreconditionFailed (412) it re-reads and retries up to `retries`
   *  times (default 3). Returning `null` from the mutator deletes the
   *  doc. Returning the same object updates it. Pass a NEW object
   *  to avoid mutating the cached value. */
  update(
    key: string,
    mutator: (current: T | null) => T | null | Promise<T | null>,
    opts?: { retries?: number },
  ): Promise<T | null>;
  /** Subscribe to changes via polling. Diffs the current snapshot
   *  against a previous one every `intervalMs` (default 5000) and
   *  emits events for added / changed / removed docs. Returns an
   *  unsubscribe function. */
  subscribe(
    handler: (events: Array<ChangeEvent<T>>) => void,
    opts?: { intervalMs?: number; onError?: (e: unknown) => void },
  ): () => void;
}

export type ChangeEvent<T> =
  | { kind: "added"; key: string; doc: T }
  | { kind: "changed"; key: string; doc: T }
  | { kind: "removed"; key: string };

/** A collection created with `partitionPrefix` — adds the scoped view.
 *  `partition(id)` returns a plain Collection rooted at that partition:
 *  bare keys work for every operation, `list`/`scan` see only that
 *  partition's records. */
export interface PartitionedCollection<T> extends Collection<T> {
  partition(id: string): Collection<T>;
}

export interface CollectionDeps {
  getClient: () => Promise<S3Client>;
  bucket: string;
  /** Path under the bucket where this collection lives. Slash-terminated.
   *  The collection appends `{key}/{filename}` to this. */
  /** Path under the bucket where this collection lives. Slash-terminated.
   *  A FUNCTION when the path depends on the session (`scope: "private"` →
   *  `private/{sub}/…`): it is resolved per operation, not at creation, so
   *  `const c = app.collection(x, { scope: "private" })` at module top level
   *  works — the user simply has to be signed in by the time they CALL it. */
  basePath: string | (() => string);
  /** When set, records are partitioned: `put` prefixes every key with
   *  `{partitionPrefix()}/` so writes land in the caller's own partition
   *  (see the header note). Resolved per WRITE, not at creation, for the
   *  same top-level-construction reason as `basePath`. The resolver's
   *  value is a single path segment (no slashes). */
  partitionPrefix?: () => string;
}

export function createCollection<T = unknown>(
  deps: CollectionDeps,
  config: CollectionConfig<T> = {},
): Collection<T> {
  const filename = config.filename ?? "index.json";
  const keyFn = config.key;

  // Index pointers live under a SIBLING prefix (`{name}-by-{index}/`), which
  // an "own records" IAM grant on the partitioned prefix does not cover — the
  // pointer write would 403 for exactly the users partitioning is for. Refuse
  // the combination up front instead of failing at the first put.
  if (deps.partitionPrefix && config.indexes?.length) {
    throw new Error(
      "collection: `indexes` cannot be combined with partitioning — index pointers " +
        "live under a sibling prefix that per-partition access does not cover. " +
        "Drop the indexes or the partition.",
    );
  }

  /** Resolve the base prefix now (see CollectionDeps.basePath). */
  function base(): string {
    return typeof deps.basePath === "function" ? deps.basePath() : deps.basePath;
  }

  function pathOf(key: string): string {
    return `${base()}${encodeKey(key)}/${filename}`;
  }

  function deriveKey(doc: T): string {
    if (!keyFn) {
      throw new Error(
        "collection.put(doc): no `key` configured — call `put(key, doc)` instead, or set `key:` in the collection config.",
      );
    }
    if (typeof keyFn === "function") return keyFn(doc);
    const v = (doc as unknown as Record<string, unknown>)[keyFn];
    if (typeof v !== "string") {
      throw new Error(
        `collection.put(doc): doc.${keyFn} is ${typeof v}, expected string (configured via key: "${keyFn}")`,
      );
    }
    return v;
  }

  async function getRaw(s3: S3Client, key: string): Promise<T | null> {
    try {
      const res = await s3.send(new GetObjectCommand({ Bucket: deps.bucket, Key: pathOf(key) }));
      const bytes = await readBytes(res.Body);
      return JSON.parse(new TextDecoder().decode(bytes)) as T;
    } catch (e) {
      if (is404(e)) return null;
      throw e;
    }
  }

  async function* scan(): AsyncIterable<T> {
    const s3 = await deps.getClient();
    let continuationToken: string | undefined;
    const trailing = `/${filename}`;
    while (true) {
      const res = await s3.send(
        new ListObjectsV2Command({
          Bucket: deps.bucket,
          Prefix: base(),
          ContinuationToken: continuationToken,
          MaxKeys: 1000,
        }),
      );
      for (const obj of res.Contents ?? []) {
        if (!obj.Key || !obj.Key.endsWith(trailing)) continue;
        try {
          const got = await s3.send(new GetObjectCommand({ Bucket: deps.bucket, Key: obj.Key }));
          const bytes = await readBytes(got.Body);
          yield JSON.parse(new TextDecoder().decode(bytes)) as T;
        } catch (e) {
          if (!is404(e)) throw e;
          // listed-but-disappeared mid-scan — skip
        }
      }
      if (!res.IsTruncated || !res.NextContinuationToken) return;
      continuationToken = res.NextContinuationToken;
    }
  }

  async function list(): Promise<T[]> {
    const out: T[] = [];
    for await (const doc of scan()) out.push(doc);
    reportScanCount("collection.list", base(), out.length);
    return out;
  }

  function whereChain(preds: Array<(doc: T) => boolean>): CollectionWhere<T> {
    return {
      async list() {
        const out: T[] = [];
        for await (const doc of scan()) {
          if (preds.every((p) => p(doc))) out.push(doc);
        }
        return out;
      },
      where(p) {
        return whereChain([...preds, p]);
      },
    };
  }

  /** Index pointer path under a parallel "{collection}-by-{index}" prefix.
   *  Sibling of `basePath` not under it, so listing the main prefix doesn't
   *  see pointers. */
  function indexPath(indexName: string, value: string): string {
    // basePath ends in `/` and looks like `s3db/shared/{name}/`. The parallel
    // prefix becomes `s3db/shared/{name}-by-{indexName}/{value}`.
    const trimmed = base().replace(/\/$/, "");
    return `${trimmed}-by-${indexName}/${encodeKey(value)}`;
  }

  function indexValue(doc: T, idx: CollectionIndex<T>): string {
    const v = (doc as unknown as Record<string, unknown>)[idx.field];
    if (typeof v !== "string") {
      throw new Error(
        `collection.put: index '${idx.name}' field '${idx.field}' is ${typeof v}, expected string`,
      );
    }
    return v;
  }

  async function readBytesOpt(s3: S3Client, key: string): Promise<Uint8Array | null> {
    try {
      const res = await s3.send(new GetObjectCommand({ Bucket: deps.bucket, Key: key }));
      return await readBytes(res.Body);
    } catch (e) {
      if (is404(e)) return null;
      throw e;
    }
  }

  async function put(...args: [T] | [string, T]): Promise<{ key: string; path: string }> {
    let key: string;
    let doc: T;
    if (args.length === 1) {
      doc = args[0];
      key = deriveKey(doc);
    } else {
      [key, doc] = args;
    }
    // Partitioned writes land in the caller's own partition; the returned
    // key is the FULL `{partition}/{key}` — valid for get/update/delete.
    if (deps.partitionPrefix) key = `${deps.partitionPrefix()}/${key}`;
    const path = pathOf(key);
    const s3 = await deps.getClient();
    const indexes = config.indexes ?? [];

    // Fetch the OLD doc so we can detect index-value changes and
    // delete stale pointers post-write.
    const oldDoc = indexes.length ? await getRaw(s3, key) : null;

    // Maintain index pointers BEFORE writing the main doc so that
    // unique constraints fail loudly without leaving a half-written
    // state. If a unique index already has a pointer for this value,
    // the If-None-Match: * causes a 412 → throw with a clear message.
    for (const idx of indexes) {
      const newVal = indexValue(doc, idx);
      const oldVal = oldDoc ? (oldDoc as unknown as Record<string, unknown>)[idx.field] : undefined;
      if (oldVal === newVal) continue; // no change
      const ip = indexPath(idx.name, newVal);
      const body = new TextEncoder().encode(key);
      try {
        await s3.send(
          new PutObjectCommand({
            Bucket: deps.bucket,
            Key: ip,
            Body: body,
            ContentType: "text/plain; charset=utf-8",
            ...(idx.unique ? { IfNoneMatch: "*" } : {}),
          }),
        );
      } catch (e) {
        if (idx.unique && isPreconditionFailed(e)) {
          throw new Error(
            `collection.put: unique index '${idx.name}' violation — '${newVal}' already in use`,
          );
        }
        throw e;
      }
    }

    try {
      await s3.send(
        new PutObjectCommand({
          Bucket: deps.bucket,
          Key: path,
          Body: JSON.stringify(doc, null, 2),
          ContentType: "application/json",
          // Claim semantics: create, never overwrite (see CollectionConfig).
          ...(config.createOnly ? { IfNoneMatch: "*" } : {}),
        }),
      );
    } catch (e) {
      if (config.createOnly && isPreconditionFailed(e)) {
        throw new Error(`collection.put: '${key}' already exists (createOnly store — claims are first-come)`);
      }
      throw e;
    }

    // Clean up stale pointers from any old indexed values.
    if (oldDoc) {
      for (const idx of indexes) {
        const oldVal = (oldDoc as unknown as Record<string, unknown>)[idx.field];
        const newVal = indexValue(doc, idx);
        if (typeof oldVal === "string" && oldVal !== newVal) {
          await s3
            .send(new DeleteObjectCommand({ Bucket: deps.bucket, Key: indexPath(idx.name, oldVal) }))
            .catch(() => {});
        }
      }
    }

    return { key, path };
  }

  async function findBy(indexName: string, value: string): Promise<T | null> {
    const idx = (config.indexes ?? []).find((i) => i.name === indexName);
    if (!idx) {
      throw new Error(`collection.findBy: no index named '${indexName}' configured`);
    }
    const s3 = await deps.getClient();
    const pointerBytes = await readBytesOpt(s3, indexPath(indexName, value));
    if (!pointerBytes) return null;
    const primaryKey = new TextDecoder().decode(pointerBytes);
    return getRaw(s3, primaryKey);
  }

  async function deleteWithIndexes(key: string): Promise<void> {
    const s3 = await deps.getClient();
    const indexes = config.indexes ?? [];
    const old = indexes.length ? await getRaw(s3, key) : null;
    await s3.send(new DeleteObjectCommand({ Bucket: deps.bucket, Key: pathOf(key) }));
    if (old) {
      for (const idx of indexes) {
        const v = (old as unknown as Record<string, unknown>)[idx.field];
        if (typeof v === "string") {
          await s3
            .send(new DeleteObjectCommand({ Bucket: deps.bucket, Key: indexPath(idx.name, v) }))
            .catch(() => {});
        }
      }
    }
  }

  async function update(
    key: string,
    mutator: (current: T | null) => T | null | Promise<T | null>,
    opts: { retries?: number } = {},
  ): Promise<T | null> {
    const maxAttempts = (opts.retries ?? 3) + 1;
    const s3 = await deps.getClient();
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      let etag: string | undefined;
      let current: T | null = null;
      try {
        const res = await s3.send(
          new GetObjectCommand({ Bucket: deps.bucket, Key: pathOf(key) }),
        );
        etag = res.ETag;
        const bytes = await readBytes(res.Body);
        current = JSON.parse(new TextDecoder().decode(bytes)) as T;
      } catch (e) {
        if (!is404(e)) throw e;
      }

      const next = await mutator(current);
      try {
        if (next === null) {
          if (current === null) return null;
          await s3.send(
            new DeleteObjectCommand({
              Bucket: deps.bucket,
              Key: pathOf(key),
              ...(etag ? { IfMatch: etag } : {}),
            } as never),
          );
          return null;
        }
        const path = pathOf(key);
        await s3.send(
          new PutObjectCommand({
            Bucket: deps.bucket,
            Key: path,
            Body: JSON.stringify(next, null, 2),
            ContentType: "application/json",
            ...(etag ? { IfMatch: etag } : { IfNoneMatch: "*" }),
          }),
        );
        return next;
      } catch (e) {
        if (isPreconditionFailed(e)) {
          // Concurrent modification. Retry while we have attempts left; on the
          // last one surface a MEANINGFUL error — a raw `PreconditionFailed`
          // tells the caller nothing about what happened or what to do.
          if (attempt < maxAttempts - 1) continue;
          throw new Error(
            `collection.update: exhausted ${maxAttempts} attempts on key '${key}' — ` +
              "the document kept being modified concurrently. Retry, or raise `retries`.",
          );
        }
        throw e;
      }
    }
    // Unreachable: the loop either returns or throws above. Kept as a guard.
    throw new Error(`collection.update: exhausted ${maxAttempts} attempts on key '${key}'`);
  }

  const collection: Collection<T> = {
    scan,
    list,
    where(p) {
      return whereChain([p]);
    },
    async get(key: string) {
      return getRaw(await deps.getClient(), key);
    },
    async has(key: string) {
      // HEAD, not GET — existence checks shouldn't pay for the body.
      const s3 = await deps.getClient();
      try {
        await s3.send(new HeadObjectCommand({ Bucket: deps.bucket, Key: pathOf(key) }));
        return true;
      } catch (e) {
        if (is404(e)) return false;
        throw e;
      }
    },
    put: put as Collection<T>["put"],
    delete: deleteWithIndexes,
    findBy,
    update,
    subscribe(handler, opts = {}) {
      const intervalMs = opts.intervalMs ?? 5000;
      let stopped = false;
      let timer: ReturnType<typeof setTimeout> | undefined;
      // Snapshot: key → ETag (cheap diff key, no body fetch unless changed)
      let snapshot = new Map<string, string>();
      let isFirst = true;

      async function tick(): Promise<void> {
        if (stopped) return;
        try {
          const s3 = await deps.getClient();
          const current = new Map<string, string>();
          let token: string | undefined;
          const trailing = `/${filename}`;
          do {
            const res = await s3.send(
              new ListObjectsV2Command({
                Bucket: deps.bucket,
                Prefix: base(),
                ContinuationToken: token,
                MaxKeys: 1000,
              }),
            );
            for (const obj of res.Contents ?? []) {
              if (!obj.Key || !obj.Key.endsWith(trailing) || !obj.ETag) continue;
              // Recover the primary key from the path layout
              //   {basePath}{key}/{filename}.
              const inner = obj.Key.slice(base().length, -trailing.length);
              current.set(decodeURIComponent(inner), obj.ETag);
            }
            token = res.IsTruncated ? res.NextContinuationToken : undefined;
          } while (token);

          if (isFirst) {
            // First tick — establish baseline only, don't fire events.
            snapshot = current;
            isFirst = false;
          } else {
            const events: Array<ChangeEvent<T>> = [];
            for (const [key, etag] of current) {
              const prev = snapshot.get(key);
              if (!prev) {
                const doc = await getRaw(s3, key);
                if (doc) events.push({ kind: "added", key, doc });
              } else if (prev !== etag) {
                const doc = await getRaw(s3, key);
                if (doc) events.push({ kind: "changed", key, doc });
              }
            }
            for (const key of snapshot.keys()) {
              if (!current.has(key)) events.push({ kind: "removed", key });
            }
            snapshot = current;
            if (events.length) handler(events);
          }
        } catch (e) {
          // Keep the loop alive (next tick retries) but SURFACE the failure —
          // a permanently-broken subscription polling silently forever was a
          // real footgun. No onError → single console.warn per tick.
          if (opts.onError) opts.onError(e);
          // eslint-disable-next-line no-console
          else console.warn("[s3p] collection.subscribe tick failed:", e);
        }
        if (!stopped) timer = setTimeout(tick, intervalMs);
      }

      tick();

      return () => {
        stopped = true;
        if (timer) clearTimeout(timer);
      };
    },
  };

  if (!deps.partitionPrefix) return collection;

  // Partition view: a plain collection rooted at one partition — bare keys,
  // partition-only listing, no further partitioning.
  const partitioned: PartitionedCollection<T> = Object.assign(collection, {
    partition(id: string): Collection<T> {
      return createCollection<T>(
        { ...deps, partitionPrefix: undefined, basePath: () => `${base()}${encodeKey(id)}/` },
        config,
      );
    },
  });
  return partitioned;
}

async function readBytes(body: unknown): Promise<Uint8Array> {
  return (body as { transformToByteArray: () => Promise<Uint8Array> }).transformToByteArray();
}

function is404(e: unknown): boolean {
  const code = (e as { name?: string; Code?: string })?.name;
  const status = (e as { $metadata?: { httpStatusCode?: number } })?.$metadata?.httpStatusCode;
  return status === 404 || code === "NoSuchKey" || code === "NotFound";
}

function isPreconditionFailed(e: unknown): boolean {
  const status = (e as { $metadata?: { httpStatusCode?: number } })?.$metadata?.httpStatusCode;
  const code = (e as { name?: string })?.name;
  return status === 412 || code === "PreconditionFailed";
}

// Allow slashes in keys (treated as sub-directories). Encode each segment
// independently so `{`, `}`, and other URL-unsafe characters are escaped.
function encodeKey(key: string): string {
  return key.split("/").map(encodeURIComponent).join("/");
}
